import { msg } from "@lingui/core/macro";
import {
  addOrderLineMutation,
  applyOrderDiscountMutation,
  createOrderMutation,
  formatMoney,
  getCategoriesQuery,
  getCombosQuery,
  getCustomersQuery,
  getEmployeesQuery,
  getModifierOptionsQuery,
  getOpenSessionQuery,
  getOrderByIdQuery,
  getOrderLinesQuery,
  getPaymentsForOrderQuery,
  getProductModifierGroupsQuery,
  getProductsQuery,
  getTablesQuery,
  getVariantsQuery,
  parseMoneyToCents,
  payOrderCashMutation,
  queryKeys,
  redeemPoints,
  redeemPointsForOrderMutation,
  remainingDue,
  removeOrderLineMutation,
  reopenOrderMutation,
  resolveProductLine,
  restoreOrderLineMutation,
  sendOrderToKitchenMutation,
  setOrderCustomerMutation,
  setTableStatusMutation,
  splitPaymentMutation,
  voidOrderMutation,
} from "@comandero/shared";
import type {
  Branch,
  Combo,
  Customer,
  DiningTable,
  Employee,
  MenuCategory,
  MenuProduct,
  ModifierOption,
  Order,
  OrderLine,
  Organization,
  Payment,
  ProductModifierGroup,
  ProductVariant,
  RegisterSession,
} from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  For,
  Show,
  untrack,
} from "solid-js";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Select, toSelectOptions } from "@/components/ui/select";
import { TextField } from "@fleet/ui";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useUndo } from "@/lib/undo";
import { EntityHistoryDialog } from "@/features/activity/components/entity-history-dialog";
import { ReceiptDialog } from "./receipt-dialog";
import { useMenuCategorySwipe } from "../hooks/use-menu-category-swipe";
import { useTicketLineSwipe } from "../hooks/use-ticket-line-swipe";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { createInitialSearchParam } from "@/lib/initial-search";

interface PosTerminalProps {
  org: () => Organization | null;
  branch: () => Branch | null;
  /** Active order from the URL (`?order=`) — shareable deep link. */
  orderParam?: Accessor<string | undefined>;
  /** Active category from the URL (`?category=`). */
  categoryParam?: Accessor<string | undefined>;
  /** Writes the active order to the URL (idempotent, `replace`). */
  onOrderChange?: (orderId: string | null) => void;
  /** Writes the active category to the URL (idempotent, `replace`). */
  onCategoryChange?: (categoryId: string | null) => void;
}

export function PosTerminal(props: PosTerminalProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  // Persist the active order ID so a POS reload restores it — the CRUD queue
  // already persisted the order in SQLite). Thus the ticket and the "Paid"
  // state survive a reload, not just the database row.
  const ACTIVE_ORDER_KEY = "comandero.activeOrderId";
  const readActiveOrder = (): string | null => {
    try {
      return globalThis.localStorage?.getItem(ACTIVE_ORDER_KEY) ?? null;
    } catch {
      return null;
    }
  };

  // Source of truth for the active order = the URL (`?order=`), so the ticket is
  // a shareable deep link. If the URL has no order (direct entry to `/pos`),
  // fall back to the pointer persisted in localStorage. We capture the initial
  // `?order` synchronously so the router's first deferred flush cannot overwrite
  // it [see lib/initial-search.ts].
  const initialOrderFromUrl = createInitialSearchParam("order");
  // Synchronous seed: the `?order` captured from the URL wins over the persisted
  // pointer. The reactive `props.orderParam` then governs it via createEffect
  // (reading it here, outside a tracked scope, would be a reactive no-op).
  const [orderId, setOrderIdRaw] = createSignal<string | null>(
    initialOrderFromUrl() ?? readActiveOrder(),
  );
  const setOrderId = (id: string | null) => {
    try {
      if (id) {
        globalThis.localStorage?.setItem(ACTIVE_ORDER_KEY, id);
      } else {
        globalThis.localStorage?.removeItem(ACTIVE_ORDER_KEY);
      }
    } catch {
      /* storage unavailable: continue in memory */
    }
    setOrderIdRaw(id);
    // Reflect the active order in the URL (idempotent: do not inflate history).
    props.onOrderChange?.(id);
  };

  // Back/forward or a link pasted while the app is running: if the URL changes
  // order externally (not from our write), seed the signal with that value.
  // CC-16: `orderId()` is read in `untrack` — the effect must depend ONLY on the
  // URL. If it tracked the signal it writes, a user `setOrderId(B)` would re-run
  // it while the URL was still A and bounce the ticket back to the old value.
  createEffect(() => {
    const fromUrl = props.orderParam?.();
    if (fromUrl !== undefined && fromUrl !== untrack(orderId)) {
      setOrderIdRaw(fromUrl);
      try {
        globalThis.localStorage?.setItem(ACTIVE_ORDER_KEY, fromUrl);
      } catch {
        /* no storage: continue in memory */
      }
    }
  });
  const [orderType, setOrderType] = createSignal<"dine_in" | "takeaway">("dine_in");
  const [selectedProduct, setSelectedProduct] = createSignal<MenuProduct | null>(null);
  const [selectedOption, setSelectedOption] = createSignal<ModifierOption | null>(null);
  const [tendered, setTendered] = createSignal("");
  const [changeSignal, setChange] = createSignal<number | null>(null);
  // Split bill + tips.
  const [splitAmount, setSplitAmount] = createSignal("");
  const [splitTip, setSplitTip] = createSignal("");
  const [sentToKitchen, setSentToKitchen] = createSignal(false);
  // Sale attribution: the employee who takes payment (for the waiter report).
  const [employeeId, setEmployeeId] = createSignal("");
  // F4: selected product variant + customer for loyalty.
  const [selectedVariant, setSelectedVariant] = createSignal<ProductVariant | null>(null);
  const [customerId, setCustomerId] = createSignal("");
  const [redeemInput, setRedeemInput] = createSignal("");
  // ACTIVITY-CORE (POS): business operations recorded with actor and reason.
  const [voidOrderOpen, setVoidOrderOpen] = createSignal(false);
  const [voidReason, setVoidReason] = createSignal("");
  const [discountOpen, setDiscountOpen] = createSignal(false);
  const [discountAmount, setDiscountAmount] = createSignal("");
  const [discountReason, setDiscountReason] = createSignal("");
  const [voidLineTarget, setVoidLineTarget] = createSignal<OrderLine | null>(null);
  const [voidLineReason, setVoidLineReason] = createSignal("");
  const [historyOpen, setHistoryOpen] = createSignal(false);
  // Printable ticket (ESC/POS): preview + payload download.
  const [receiptOpen, setReceiptOpen] = createSignal(false);
  const { registerUndo } = useUndo();

  const orgId = (): string | null => props.org()?.id ?? null;
  const branchId = (): string | null => props.branch()?.id ?? null;

  const products = createPowerSyncQuery<MenuProduct>({
    queryKey: () => queryKeys.comandero.products(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getProductsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const categories = createPowerSyncQuery<MenuCategory>({
    queryKey: () => queryKeys.comandero.categories(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCategoriesQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  // Category tabs: a leading "All" page, then one page per category. The active
  // page filters the product grid and is paged either by tapping a tab or by
  // swiping the grid (carousel). `id === null` is the "All" page.
  const initialCategoryFromUrl = createInitialSearchParam("category");
  const [categoryPageRaw, setCategoryPageRaw] = createSignal(0);
  const categoryPages = createMemo<{ id: string | null; name: string }[]>(() => [
    { id: null, name: t(msg`All`) },
    ...(categories.data ?? []).map((c) => ({ id: c.id, name: c.name ?? "" })),
  ]);

  // The active category is deep-linkable by ID (`?category=`). We resolve the ID
  // to a page index once categories have loaded; until then the raw signal wins.
  // Idempotent: reloading the URL reconstructs the tab.
  const categoryPage: Accessor<number> = createMemo(() => {
    const fromUrl = props.categoryParam?.() ?? initialCategoryFromUrl();
    if (fromUrl) {
      const idx = categoryPages().findIndex((p) => p.id === fromUrl);
      if (idx !== -1) {
        return idx;
      }
    }
    return categoryPageRaw();
  });
  const setCategoryPage = (i: number) => {
    setCategoryPageRaw(i);
    const id = categoryPages()[i]?.id ?? null;
    props.onCategoryChange?.(id);
  };
  // Clamp the active page if categories change underneath it [e.g. deletion].
  const activeCategoryId = createMemo<string | null>(() => {
    const pages = categoryPages();
    const page = pages[Math.min(categoryPage(), pages.length - 1)];
    return page?.id ?? null;
  });
  const visibleProducts = createMemo<MenuProduct[]>(() => {
    const all = products.data ?? [];
    const catId = activeCategoryId();
    return catId === null ? all : all.filter((p) => p.category_id === catId);
  });

  const employees = createPowerSyncQuery<Employee>({
    queryKey: () => queryKeys.comandero.employees(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getEmployeesQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  // Modifier groups linked to the selected product → option list for selection.
  const linkedGroups = createPowerSyncQuery<ProductModifierGroup>({
    queryKey: () => queryKeys.comandero.productModifierGroups(selectedProduct()?.id ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      const p = selectedProduct();
      return d && id && p?.id ? getProductModifierGroupsQuery(d, id, p.id) : null;
    },
    db,
    enabled: () => Boolean(selectedProduct()?.id),
  });

  const firstGroupId = createMemo<string | null>(
    () => linkedGroups.data?.[0]?.modifier_group_id ?? null,
  );

  const options = createPowerSyncQuery<ModifierOption>({
    queryKey: () => queryKeys.comandero.modifierOptions(firstGroupId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      const g = firstGroupId();
      return d && id && g ? getModifierOptionsQuery(d, id, g) : null;
    },
    db,
    enabled: () => Boolean(firstGroupId()),
  });

  // F4: variants of the selected product (sizes with their own price).
  const variants = createPowerSyncQuery<ProductVariant>({
    queryKey: () => queryKeys.comandero.variants(selectedProduct()?.id ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const p = selectedProduct();
      return d && p?.id ? getVariantsQuery(d, p.id) : null;
    },
    db,
    enabled: () => Boolean(selectedProduct()?.id),
  });

  // F4: available combos (pseudo-products at a fixed price).
  const combos = createPowerSyncQuery<Combo>({
    queryKey: () => queryKeys.comandero.combos(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCombosQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  // F4: customers for loyalty.
  const customers = createPowerSyncQuery<Customer>({
    queryKey: () => queryKeys.comandero.customers(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCustomersQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const lines = createPowerSyncQuery<OrderLine>({
    queryKey: () => queryKeys.comandero.orderLines(orderId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid = orderId();
      return d && oid ? getOrderLinesQuery(d, oid) : null;
    },
    db,
    enabled: () => Boolean(orderId()),
  });

  const currentOrder = createPowerSyncQuery<Order>({
    queryKey: () => queryKeys.comandero.order(orderId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid = orderId();
      return d && oid ? getOrderByIdQuery(d, oid) : null;
    },
    db,
    enabled: () => Boolean(orderId()),
  });

  const payments = createPowerSyncQuery<Payment>({
    queryKey: () => queryKeys.comandero.orderPayments(orderId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid = orderId();
      return d && oid ? getPaymentsForOrderQuery(d, oid) : null;
    },
    db,
    enabled: () => Boolean(orderId()),
  });

  const order = createMemo<Order | null>(() => currentOrder.data?.[0] ?? null);
  const isPaid = createMemo(() => order()?.status === "paid");

  // Branch tables: to print the table's visible name on the ticket.
  const branchTables = createPowerSyncQuery<DiningTable>({
    queryKey: () => queryKeys.comandero.tables(branchId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid0 = orgId();
      const bid = branchId();
      return d && oid0 && bid ? getTablesQuery(d, oid0, bid) : null;
    },
    db,
    enabled: () => Boolean(branchId()),
  });
  const receiptTableName = createMemo<string | null>(() => {
    const tid = order()?.table_id;
    if (!tid) {
      return null;
    }
    return (branchTables.data ?? []).find((tbl) => tbl.id === tid)?.name ?? null;
  });
  const receiptEmployeeName = createMemo<string | null>(() => {
    const eid = order()?.employee_id;
    if (!eid) {
      return null;
    }
    return (employees.data ?? []).find((emp) => emp.id === eid)?.name ?? null;
  });

  // Active shift (the branch's open cash session): new orders are born linked
  // to it so the ENTIRE trail (lines, voids, discounts, payments) is recorded
  // BY SHIFT (cash-close view in /activity?session=).
  const openSession = createPowerSyncQuery<RegisterSession>({
    queryKey: () => queryKeys.comandero.openSession(branchId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid0 = orgId();
      const bid = branchId();
      return d && oid0 && bid ? getOpenSessionQuery(d, oid0, bid) : null;
    },
    db,
    enabled: () => Boolean(branchId()),
  });

  // The active ticket is restored from localStorage after a reload. If that ID
  // points to a deleted order / one not yet synced to this device / one from
  // another restaurant, it must NOT block the POS: a restored ticket pointer
  // that no longer resolves is DISCARDED and we return to "Start a new order"
  // (a POS can always start a new order). Without this, after creating a
  // restaurant with an old pointer in localStorage, the POS stayed on
  // "Order not found" instead of showing the order start screen.
  const orderResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(order()),
    isPending: () => currentOrder.isLoading,
  });
  createEffect(() => {
    if (!orderId()) {
      return;
    }
    // Multi-restaurant isolation: the active ticket pointer is GLOBAL
    // (one key in localStorage / `?order=`), but an order belongs to
    // ONE restaurant. After switching restaurants in the switcher, that pointer
    // would resolve to an order from the PREVIOUS restaurant (its data remains
    // local) and the active restaurant's POST would show —and charge against
    // the cash register of— a foreign order. If the resolved order does not belong
    // to the active `org()`, discard it as if it did not exist (a POS can always
    // start fresh).
    const resolved = order();
    const activeOrg = orgId();
    if (resolved && activeOrg && resolved.organization_id !== activeOrg) {
      setOrderId(null);
      setChange(null);
      return;
    }
    const state = orderResolution();
    if (state === "not-found" || state === "offline") {
      setOrderId(null);
      setChange(null);
    }
  });

  // Change shown: the one from the payment action just performed, or (after a
  // reload) the `change_given` from the persisted payment.
  const change = createMemo<number | null>(() => {
    const live = changeSignal();
    if (live !== null) {
      return live;
    }
    return payments.data?.[0]?.change_given ?? null;
  });

  async function startOrder() {
    const d = drizzle();
    const oid0 = orgId();
    const bid = branchId();
    if (!d || !oid0 || !bid) {
      return;
    }
    const id = await createOrderMutation(d, oid0, bid, {
      type: orderType(),
      // Link the order to the open shift (cash-register record, anti-fraud).
      registerSessionId: openSession.data?.[0]?.id ?? null,
    });
    setOrderId(id);
    setChange(null);
  }

  async function addSelected() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    const p = selectedProduct();
    if (!d || !oid0 || !oid || !p) {
      return;
    }
    const opt = selectedOption();
    // F4: if a variant is selected, its ABSOLUTE price replaces the base and
    // the line name reflects the variant (e.g. "Coke (Large)").
    const { unitPrice, lineName } = resolveProductLine(p, selectedVariant());
    try {
      await addOrderLineMutation(
        d,
        oid0,
        oid,
        { id: p.id, name: lineName, unitPrice },
        {
          qty: 1,
          taxRate: p.tax_rate ?? 0,
          modifiers: opt
            ? [{ optionId: opt.id, name: opt.name ?? "", priceDelta: opt.price_delta ?? 0 }]
            : [],
        },
      );
    } catch (err: unknown) {
      console.error("Error adding order line:", err);
      return;
    }
    setSelectedProduct(null);
    setSelectedOption(null);
    setSelectedVariant(null);
  }

  /** F4: adds a combo as a line at the combo's fixed price. */
  async function addCombo(combo: Combo) {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    if (!d || !oid0 || !oid) {
      return;
    }
    try {
      await addOrderLineMutation(
        d,
        oid0,
        oid,
        { id: null, name: combo.name ?? "", unitPrice: combo.price ?? 0 },
        { qty: 1, taxRate: combo.tax_rate ?? 0 },
      );
    } catch (err: unknown) {
      console.error("Error adding combo line:", err);
    }
  }

  /** F4: associates a customer with the order (to accrue points at checkout). */
  async function assignCustomer(id: string) {
    setCustomerId(id);
    const d = drizzle();
    const oid = orderId();
    if (!d || !oid) {
      return;
    }
    try {
      await setOrderCustomerMutation(d, oid, id || null);
    } catch (err: unknown) {
      console.error("Error assigning customer to order:", err);
    }
  }

  /** F4: redeems customer points as an order discount. */
  async function applyRedeem() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    const cid = customerId();
    if (!d || !oid0 || !oid || !cid) {
      return;
    }
    const cust = (customers.data ?? []).find((c) => c.id === cid);
    const requested = Math.trunc(Number(redeemInput() || "0"));
    const total = order()?.total ?? 0;
    const { pointsToRedeem, discountCents } = redeemPoints(requested, cust?.points ?? 0, total);
    if (pointsToRedeem <= 0) {
      return;
    }
    try {
      await redeemPointsForOrderMutation(d, oid0, oid, cid, pointsToRedeem, discountCents);
    } catch (err: unknown) {
      console.error("Error redeeming loyalty points:", err);
      return;
    }
    setRedeemInput("");
  }

  async function charge() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    if (!d || !oid0 || !oid) {
      return;
    }
    const cents = parseMoneyToCents(tendered());
    let result: Awaited<ReturnType<typeof payOrderCashMutation>>;
    try {
      result = await payOrderCashMutation(d, oid0, oid, cents, {
        method: "cash",
        employeeId: employeeId() || null,
      });
    } catch (err: unknown) {
      console.error("Error charging order:", err);
      return;
    }
    setChange(result.change);
    const tableId = order()?.table_id ?? null;
    if (tableId) {
      try {
        await setTableStatusMutation(d, tableId, "free");
      } catch (err: unknown) {
        console.error("Error freeing table after charge:", err);
      }
    }
  }

  // Balance remaining on the bill (total − recorded payments), in cents.
  const remaining = createMemo<number>(() => {
    const total = order()?.total ?? 0;
    const paid = (payments.data ?? []).map((p) => p.amount ?? 0);
    return remainingDue(total, paid);
  });

  async function sendToKitchen() {
    const d = drizzle();
    const oid = orderId();
    if (!d || !oid) {
      return;
    }
    try {
      await sendOrderToKitchenMutation(d, oid);
    } catch (err: unknown) {
      console.error("Error sending order to kitchen:", err);
      return;
    }
    setSentToKitchen(true);
  }

  /**
   * Removes a line from the ticket (button or swipe-to-remove).
   * POS POLICY: in an OPEN order this is a normal edit (with L1 Undo); in an
   * ALREADY ORDERED (`sent`) order it is a VOID — it asks for a reason and is
   * recorded with the actor in history (without undo).
   */
  function removeLine(line: OrderLine) {
    // `sentToKitchen()` covers the window between ordering and the local row
    // reflecting status="sent" (the optimistic signal is the user's intent).
    if (order()?.status === "sent" || sentToKitchen()) {
      setVoidLineReason("");
      setVoidLineTarget(line);
      return;
    }
    void (async () => {
      const d = drizzle();
      const oid0 = orgId();
      const oid = orderId();
      if (!d || !oid0 || !oid) {
        return;
      }
      try {
        await removeOrderLineMutation(d, oid0, oid, line.id);
      } catch (err: unknown) {
        console.error("Error removing order line:", err);
        return;
      }
      // oxlint-disable-next-line lingui/no-single-variables-to-translate -- the phrase has text; {name} is an ICU placeholder
      registerUndo(t(msg`Item removed: {name}`, { name: line.name ?? "" }), async () => {
        try {
          await restoreOrderLineMutation(d, oid0, oid, line.id);
        } catch (err: unknown) {
          console.error("Error restoring removed order line:", err);
        }
      });
    })();
  }

  /** Confirms the void of an ordered line (reason optional). */
  async function confirmVoidLine() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    const line = voidLineTarget();
    if (!d || !oid0 || !oid || !line) {
      return;
    }
    try {
      await removeOrderLineMutation(d, oid0, oid, line.id, {
        reason: voidLineReason() || undefined,
      });
    } catch (err: unknown) {
      console.error("Error voiding order line:", err);
      return;
    }
    setVoidLineTarget(null);
  }

  /** Voids the entire order (reason optional; frees the table). */
  async function confirmVoidOrder() {
    const d = drizzle();
    const oid = orderId();
    if (!d || !oid) {
      return;
    }
    try {
      await voidOrderMutation(d, oid, voidReason() || undefined);
      const tableId = order()?.table_id ?? null;
      if (tableId) {
        await setTableStatusMutation(d, tableId, "free");
      }
    } catch (err: unknown) {
      console.error("Error voiding order:", err);
      return;
    }
    setVoidOrderOpen(false);
    setOrderId(null);
    setChange(null);
  }

  /** Applies a ticket discount (amount + optional reason). */
  async function confirmDiscount() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    if (!d || !oid0 || !oid) {
      return;
    }
    const cents = parseMoneyToCents(discountAmount());
    try {
      await applyOrderDiscountMutation(d, oid0, oid, cents, discountReason() || undefined);
    } catch (err: unknown) {
      console.error("Error applying order discount:", err);
      return;
    }
    setDiscountOpen(false);
    setDiscountAmount("");
    setDiscountReason("");
  }

  /** Reopens a paid order (recorded operation, NOT an undo). */
  async function reopenOrder() {
    const d = drizzle();
    const oid = orderId();
    if (!d || !oid) {
      return;
    }
    try {
      await reopenOrderMutation(d, oid);
    } catch (err: unknown) {
      console.error("Error reopening order:", err);
      return;
    }
    setChange(null);
  }

  /** Records a partial (split) payment with optional tip. */
  async function paySplit() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    if (!d || !oid0 || !oid) {
      return;
    }
    const amount = parseMoneyToCents(splitAmount());
    const tip = parseMoneyToCents(splitTip());
    if (amount <= 0) {
      return;
    }
    let result: Awaited<ReturnType<typeof splitPaymentMutation>>;
    try {
      result = await splitPaymentMutation(d, oid0, oid, amount, {
        method: "cash",
        tip,
        employeeId: employeeId() || null,
      });
    } catch (err: unknown) {
      console.error("Error registering split payment:", err);
      return;
    }
    setSplitAmount("");
    setSplitTip("");
    if (result.settled) {
      setChange(result.change);
      const tableId = order()?.table_id ?? null;
      if (tableId) {
        try {
          await setTableStatusMutation(d, tableId, "free");
        } catch (err: unknown) {
          console.error("Error freeing table after split payment:", err);
        }
      }
    }
  }

  return (
    <div class="flex flex-col gap-6" data-testid="pos-terminal">
      <Show
        when={orderId()}
        fallback={
          <div class="flex flex-col items-center justify-center gap-5 rounded-2xl border border-dashed border-border/60 bg-surface/40 px-6 py-16 text-center">
            <div class="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/12 text-accent">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="h-8 w-8"
                aria-hidden="true"
              >
                <path d="M12 5v14M5 12h14" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div>
              <p class="text-lg font-semibold text-foreground">{t(msg`Start a new order`)}</p>
              <p class="mt-1 text-sm text-muted-foreground">
                {t(msg`Pick the order type and tap to begin.`)}
              </p>
            </div>
            <div class="flex flex-wrap items-center justify-center gap-3">
              <Select
                data-testid="order-type-select"
                aria-label={t(msg`Order type`)}
                size="lg"
                value={orderType()}
                onChange={(v) => setOrderType(v === "takeaway" ? "takeaway" : "dine_in")}
                options={[
                  { value: "dine_in", label: t(msg`Dine-in`) },
                  { value: "takeaway", label: t(msg`Takeaway`) },
                ]}
              />
              <Button size="touch" data-testid="new-order-btn" onClick={() => void startOrder()}>
                {t(msg`New order`)}
              </Button>
            </div>
          </div>
        }
      >
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Product grid */}
          <section>
            <h3 class="mb-2 text-sm font-semibold text-muted-foreground">{t(msg`Products`)}</h3>
            <Show when={categoryPages().length > 1}>
              <ProductCategoryTabs
                pages={categoryPages}
                active={categoryPage}
                onSelect={(i) => {
                  setCategoryPage(i);
                  setSelectedProduct(null);
                }}
              />
            </Show>
            <ProductGrid
              pages={categoryPages}
              activePage={categoryPage}
              setActivePage={(i) => {
                setCategoryPage(i);
                setSelectedProduct(null);
              }}
              products={visibleProducts}
              selectedId={() => selectedProduct()?.id ?? null}
              onPick={(p) => {
                setSelectedProduct(p);
                setSelectedOption(null);
                setSelectedVariant(null);
              }}
            />

            {/* F4: combos a precio fijo — un toque agrega la línea */}
            <Show when={(combos.data ?? []).length > 0}>
              <div class="mt-3" data-testid="combo-picker">
                <p class="mb-1 text-xs text-muted-foreground">{t(msg`Combos`)}</p>
                <div class="flex flex-wrap gap-2">
                  <For each={combos.data}>
                    {(c) => (
                      <button
                        type="button"
                        data-testid="pos-combo"
                        data-combo-name={c.name}
                        class="flex min-h-16 flex-col justify-between rounded-xl border border-border/60 bg-surface px-3.5 py-2.5 text-left transition-[transform,border-color,background-color] hover:border-accent/60 hover:bg-accent/5 active:scale-[0.98]"
                        onClick={() => void addCombo(c)}
                      >
                        <div class="text-sm font-semibold">{c.name}</div>
                        <div class="mt-1 font-numeric text-sm font-bold text-accent">
                          {formatMoney(c.price)}
                        </div>
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            <Show when={selectedProduct()}>
              <div class="mt-3" data-testid="modifier-picker">
                {/* F4: variantes (tamaños con precio propio) */}
                <Show when={(variants.data ?? []).length > 0}>
                  <p class="mb-1 text-xs text-muted-foreground">{t(msg`Variant`)}</p>
                  <div class="mb-2 flex flex-wrap gap-2">
                    <For each={variants.data}>
                      {(v) => (
                        <button
                          type="button"
                          data-testid="variant-option"
                          data-variant-name={v.name}
                          class="rounded-md border border-border/40 px-2 py-1 text-xs"
                          classList={{ "ring-2 ring-ring": selectedVariant()?.id === v.id }}
                          onClick={() => setSelectedVariant(v)}
                        >
                          {v.name} ({formatMoney(v.price)})
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
                <Show when={(options.data ?? []).length > 0}>
                  <p class="mb-1 text-xs text-muted-foreground">{t(msg`Modifier`)}</p>
                  <div class="flex flex-wrap gap-2">
                    <For each={options.data}>
                      {(o) => (
                        <button
                          type="button"
                          data-testid="modifier-option"
                          data-option-name={o.name}
                          class="rounded-md border border-border/40 px-2 py-1 text-xs"
                          classList={{ "ring-2 ring-ring": selectedOption()?.id === o.id }}
                          onClick={() => setSelectedOption(o)}
                        >
                          {o.name} (+{formatMoney(o.price_delta)})
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
                <Button
                  size="touch"
                  class="mt-3 w-full"
                  data-testid="add-line-btn"
                  onClick={() => void addSelected()}
                >
                  {t(msg`Add to order`)}
                </Button>
              </div>
            </Show>
          </section>

          {/* Ticket */}
          <section class="rounded-2xl border border-border/60 bg-surface p-4 shadow-sm shadow-black/5">
            <h3 class="mb-3 text-sm font-semibold text-muted-foreground">{t(msg`Ticket`)}</h3>
            <ul class="flex flex-col divide-y divide-border/40" data-testid="ticket-lines">
              <Show
                when={(lines.data ?? []).length > 0}
                fallback={
                  <li class="py-6 text-center text-sm text-muted-foreground">
                    {t(msg`No items yet. Tap a product to add it.`)}
                  </li>
                }
              >
                <For each={lines.data}>
                  {(l) => (
                    <TicketLine
                      line={l}
                      canRemove={() => !isPaid()}
                      onRemove={() => removeLine(l)}
                    />
                  )}
                </For>
              </Show>
            </ul>
            <div class="mt-3 space-y-1.5 border-t border-border/60 pt-3 text-sm">
              <div class="flex justify-between text-muted-foreground">
                <span>{t(msg`Subtotal`)}</span>
                <span class="font-numeric tabular-nums" data-testid="order-subtotal">
                  {formatMoney(order()?.subtotal)}
                </span>
              </div>
              <div class="flex justify-between text-muted-foreground">
                <span>{t(msg`Tax`)}</span>
                <span class="font-numeric tabular-nums" data-testid="order-tax">
                  {formatMoney(order()?.tax_total)}
                </span>
              </div>
              <div class="flex items-baseline justify-between pt-1 text-lg font-bold">
                <span>{t(msg`Total`)}</span>
                <span class="font-numeric tabular-nums text-accent" data-testid="order-total">
                  {formatMoney(order()?.total)}
                </span>
              </div>
              <div
                class="flex justify-between text-sm text-muted-foreground"
                data-testid="order-remaining-row"
              >
                <span>{t(msg`Remaining`)}</span>
                <span class="font-numeric tabular-nums" data-testid="order-remaining">
                  {formatMoney(remaining())}
                </span>
              </div>
            </div>

            <Show when={!isPaid() && (lines.data ?? []).length > 0}>
              <div class="mt-3">
                <Button
                  variant="secondary"
                  size="touch"
                  class="w-full"
                  data-testid="send-to-kitchen-btn"
                  disabled={sentToKitchen() || order()?.status === "sent"}
                  onClick={() => void sendToKitchen()}
                >
                  <Show
                    when={sentToKitchen() || order()?.status === "sent"}
                    fallback={t(msg`Send to kitchen`)}
                  >
                    <span data-testid="sent-to-kitchen-badge">{t(msg`Sent to kitchen`)}</span>
                  </Show>
                </Button>
              </div>
            </Show>

            {/* ACTIVITY-CORE (POS): operaciones de negocio registradas con actor. */}
            <Show when={!isPaid() && order()}>
              <div class="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  class="flex-1"
                  data-testid="order-discount-btn"
                  onClick={() => setDiscountOpen(true)}
                >
                  {t(msg`Discount`)}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  class="flex-1 text-destructive"
                  data-testid="order-void-btn"
                  onClick={() => {
                    setVoidReason("");
                    setVoidOrderOpen(true);
                  }}
                >
                  {t(msg`Void order`)}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="order-history-btn"
                  onClick={() => setHistoryOpen(true)}
                >
                  {t(msg`History`)}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="print-ticket-btn"
                  onClick={() => setReceiptOpen(true)}
                >
                  {t(msg`Ticket`)}
                </Button>
              </div>
            </Show>

            <Show
              when={!isPaid()}
              fallback={
                <div
                  class="mt-4 rounded-xl border border-success/40 bg-success/10 p-4 text-sm"
                  data-testid="paid-banner"
                >
                  <div class="flex items-center gap-2 text-base font-bold text-success">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      class="h-5 w-5"
                      aria-hidden="true"
                    >
                      <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    {t(msg`Paid`)}
                  </div>
                  <div class="mt-1">
                    {t(msg`Change`)}:{" "}
                    <span
                      class="font-numeric tabular-nums font-semibold"
                      data-testid="change-amount"
                    >
                      {formatMoney(change())}
                    </span>
                  </div>
                  <Button
                    size="touch"
                    class="mt-3 w-full"
                    data-testid="new-order-after-paid-btn"
                    onClick={() => {
                      setOrderId(null);
                      setChange(null);
                      setCustomerId("");
                      setRedeemInput("");
                      setSentToKitchen(false);
                    }}
                  >
                    {t(msg`New order`)}
                  </Button>
                  {/* Reapertura EXPLÍCITA (no undo): queda en el historial con actor. */}
                  <div class="mt-2 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      class="flex-1"
                      data-testid="reopen-order-btn"
                      onClick={() => void reopenOrder()}
                    >
                      {t(msg`Reopen order`)}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="order-history-btn-paid"
                      onClick={() => setHistoryOpen(true)}
                    >
                      {t(msg`History`)}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="print-ticket-btn-paid"
                      onClick={() => setReceiptOpen(true)}
                    >
                      {t(msg`Print ticket`)}
                    </Button>
                  </div>
                </div>
              }
            >
              {/* Atribución de venta al empleado que cobra (reporte por mesero). */}
              <Show when={(employees.data ?? []).length > 0}>
                <div class="mt-4">
                  <p class="mb-1 text-xs text-muted-foreground">{t(msg`Sold by`)}</p>
                  <Select
                    data-testid="pos-employee-select"
                    aria-label={t(msg`Sold by`)}
                    fluid
                    value={employeeId()}
                    onChange={setEmployeeId}
                    options={[
                      { value: "", label: t(msg`Unassigned`) },
                      ...toSelectOptions(
                        employees.data,
                        (emp) => emp.id,
                        (emp) => emp.name,
                      ),
                    ]}
                  />
                </div>
              </Show>

              {/* F4: cliente + canje de puntos de fidelización */}
              <Show when={(customers.data ?? []).length > 0}>
                <div class="mt-4" data-testid="loyalty-section">
                  <p class="mb-1 text-xs text-muted-foreground">{t(msg`Customer`)}</p>
                  <Select
                    data-testid="pos-customer-select"
                    aria-label={t(msg`Customer`)}
                    fluid
                    value={customerId()}
                    onChange={(v) => void assignCustomer(v)}
                    options={[
                      { value: "", label: t(msg`Walk-in`) },
                      ...(customers.data ?? []).map((c) => ({
                        value: c.id,
                        label: `${c.name} (${c.points ?? 0} ${t(msg`pts`)})`,
                      })),
                    ]}
                  />
                  <Show when={customerId()}>
                    <div class="mt-2 flex items-center gap-2">
                      <TextField
                        data-testid="redeem-points-input"
                        placeholder={t(msg`Redeem points`)}
                        inputmode="numeric"
                        value={redeemInput()}
                        onInput={(e) => setRedeemInput(e.currentTarget.value)}
                      />
                      <Button
                        variant="secondary"
                        data-testid="redeem-points-btn"
                        onClick={() => void applyRedeem()}
                      >
                        {t(msg`Redeem`)}
                      </Button>
                    </div>
                  </Show>
                </div>
              </Show>

              <div class="mt-4 flex items-center gap-2">
                <TextField
                  data-testid="tendered-input"
                  placeholder={t(msg`Cash received`)}
                  inputmode="decimal"
                  value={tendered()}
                  onInput={(e) => setTendered(e.currentTarget.value)}
                />
                <Button size="touch" data-testid="charge-btn" onClick={() => void charge()}>
                  {t(msg`Charge cash`)}
                </Button>
              </div>

              {/* Split bill + tips: registra pagos parciales con propina. */}
              <div class="mt-4 border-t border-border/40 pt-3" data-testid="split-section">
                <p class="mb-2 text-xs font-semibold text-muted-foreground">
                  {t(msg`Split bill / tip`)}
                </p>
                <div class="flex flex-wrap items-center gap-2">
                  <TextField
                    data-testid="split-amount-input"
                    placeholder={t(msg`Amount`)}
                    inputmode="decimal"
                    value={splitAmount()}
                    onInput={(e) => setSplitAmount(e.currentTarget.value)}
                  />
                  <TextField
                    data-testid="split-tip-input"
                    placeholder={t(msg`Tip`)}
                    inputmode="decimal"
                    value={splitTip()}
                    onInput={(e) => setSplitTip(e.currentTarget.value)}
                  />
                  <Button
                    variant="secondary"
                    data-testid="split-pay-btn"
                    onClick={() => void paySplit()}
                  >
                    {t(msg`Add payment`)}
                  </Button>
                </div>
              </div>
            </Show>
          </section>
        </div>
      </Show>

      {/* ── ACTIVITY-CORE: diálogos de operaciones registradas ───────────── */}
      {/* Anular ORDEN (motivo opcional; queda en el historial con actor). */}
      <Dialog open={voidOrderOpen()} onOpenChange={setVoidOrderOpen} contentClass="max-w-sm">
        <div class="space-y-4" data-testid="void-order-dialog">
          <div>
            <h2 class="text-lg font-semibold">{t(msg`Void this order?`)}</h2>
            <p class="mt-1 text-sm text-muted-foreground">
              {t(
                msg`The order will be cancelled. This is recorded in the activity log with your name.`,
              )}
            </p>
          </div>
          <TextField
            value={voidReason()}
            onInput={(e) => setVoidReason(e.currentTarget.value)}
            placeholder={t(msg`Reason (optional)`)}
            data-testid="void-order-reason"
          />
          <div class="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setVoidOrderOpen(false)}>
              {t(msg`Cancel`)}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="void-order-confirm"
              onClick={() => void confirmVoidOrder()}
            >
              {t(msg`Void order`)}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Anular LÍNEA comandada (motivo opcional). */}
      <Dialog
        open={Boolean(voidLineTarget())}
        onOpenChange={(open) => {
          if (!open) {
            setVoidLineTarget(null);
          }
        }}
        contentClass="max-w-sm"
      >
        <div class="space-y-4" data-testid="void-line-dialog">
          <div>
            <h2 class="text-lg font-semibold">
              {/* oxlint-disable-next-line lingui/no-single-variables-to-translate -- sentence has text; {name} is ICU placeholder */}
              {t(msg`Void "{name}"?`, { name: voidLineTarget()?.name ?? "" })}
            </h2>
            <p class="mt-1 text-sm text-muted-foreground">
              {t(
                msg`This item was already sent to the kitchen. Voiding it is recorded with your name.`,
              )}
            </p>
          </div>
          <TextField
            value={voidLineReason()}
            onInput={(e) => setVoidLineReason(e.currentTarget.value)}
            placeholder={t(msg`Reason (optional)`)}
            data-testid="void-line-reason"
          />
          <div class="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setVoidLineTarget(null)}>
              {t(msg`Cancel`)}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="void-line-confirm"
              onClick={() => void confirmVoidLine()}
            >
              {t(msg`Void item`)}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Descuento de ticket (monto + motivo opcional). */}
      <Dialog open={discountOpen()} onOpenChange={setDiscountOpen} contentClass="max-w-sm">
        <div class="space-y-4" data-testid="discount-dialog">
          <div>
            <h2 class="text-lg font-semibold">{t(msg`Apply a discount`)}</h2>
            <p class="mt-1 text-sm text-muted-foreground">
              {t(msg`Discounts are recorded in the activity log with your name and reason.`)}
            </p>
          </div>
          <TextField
            value={discountAmount()}
            onInput={(e) => setDiscountAmount(e.currentTarget.value)}
            placeholder={t(msg`Amount`)}
            inputmode="decimal"
            data-testid="discount-amount"
          />
          <TextField
            value={discountReason()}
            onInput={(e) => setDiscountReason(e.currentTarget.value)}
            placeholder={t(msg`Reason (optional)`)}
            data-testid="discount-reason"
          />
          <div class="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDiscountOpen(false)}>
              {t(msg`Cancel`)}
            </Button>
            <Button size="sm" data-testid="discount-confirm" onClick={() => void confirmDiscount()}>
              {t(msg`Apply discount`)}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Ticket imprimible: preview + payload ESC/POS descargable. */}
      <ReceiptDialog
        open={receiptOpen}
        onOpenChange={setReceiptOpen}
        order={order}
        lines={() => lines.data ?? []}
        payments={() => payments.data ?? []}
        restaurantName={() => props.org()?.name ?? ""}
        branchName={() => props.branch()?.name ?? null}
        tableName={receiptTableName}
        employeeName={receiptEmployeeName}
      />

      {/* Historial de la orden (sheet, revert solo si la política lo permite). */}
      <Show when={orderId()}>
        {(oid) => (
          <EntityHistoryDialog
            open={historyOpen()}
            onOpenChange={setHistoryOpen}
            entityType="order"
            entityId={oid()}
            canRevert={!isPaid()}
          />
        )}
      </Show>
    </div>
  );
}

/**
 * A ticket line with swipe-to-remove (premium, consistent with the shared
 * lib/gestures standard). Swipe-left commits the deletion; a delete button visible
 * on hover/focus keeps keyboard and mouse access intact. Only while the order
 * is not paid.
 */
function TicketLine(props: { line: OrderLine; canRemove: () => boolean; onRemove: () => void }) {
  const { t } = useI18n();
  let surfaceRef: HTMLDivElement | undefined;

  const swipe = useTicketLineSwipe({
    enabled: () => props.canRemove(),
    onRemove: () => props.onRemove(),
    getSurfaceRef: () => surfaceRef,
  });

  return (
    <li
      class="relative animate-completed-reveal overflow-hidden"
      data-testid="ticket-line"
      data-line-id={props.line.id}
    >
      {/* Underlay: affordance de borrado que se revela al deslizar a la izquierda. */}
      <Show when={props.canRemove()}>
        <div
          class="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end gap-1.5 pr-3 text-sm font-semibold text-destructive"
          aria-hidden="true"
          style={{ opacity: swipe.tx() < 0 ? 1 : 0 }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="h-4 w-4"
          >
            <path
              d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {t(msg`Remove`)}
        </div>
      </Show>

      <div
        ref={(el) => {
          surfaceRef = el;
        }}
        class="group flex items-center justify-between gap-3 bg-surface py-2 text-sm touch-pan-y"
        style={{
          transform: `translate3d(${swipe.tx()}px, 0, 0)`,
          transition: swipe.isAnimating()
            ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)"
            : "none",
        }}
        onPointerDown={swipe.handlePointerDown}
        onPointerMove={swipe.handlePointerMove}
        onPointerUp={swipe.handlePointerUp}
        onPointerCancel={swipe.handlePointerCancel}
      >
        <span class="font-medium">
          <span class="font-numeric text-muted-foreground">{props.line.qty}×</span>{" "}
          {props.line.name}
        </span>
        <span class="flex items-center gap-2">
          <span class="font-numeric tabular-nums">{formatMoney(props.line.line_total)}</span>
          <Show when={props.canRemove()}>
            <button
              type="button"
              data-testid="remove-line-btn"
              aria-label={t(msg`Remove line`)}
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
              onClick={() => props.onRemove()}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </Show>
        </span>
      </div>
    </li>
  );
}

interface CategoryPage {
  id: string | null;
  name: string;
}

/**
 * Category tab strip for the POS product grid. The accessible, primary way to
 * change category (tap or keyboard); the grid swipe is an additive shortcut that
 * drives the same `onSelect`. Implemented as an ARIA tablist so arrow keys and
 * screen readers navigate the categories.
 */
function ProductCategoryTabs(props: {
  pages: Accessor<CategoryPage[]>;
  active: Accessor<number>;
  onSelect: (index: number) => void;
}) {
  const { t } = useI18n();
  return (
    <div
      role="tablist"
      aria-label={t(msg`Menu categories`)}
      class="-mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-1"
      data-testid="pos-category-tabs"
    >
      <For each={props.pages()}>
        {(page, index) => {
          const isActive = () => props.active() === index();
          return (
            <button
              type="button"
              role="tab"
              aria-selected={isActive()}
              tabindex={isActive() ? 0 : -1}
              data-testid="pos-category-tab"
              data-category-name={page.name}
              class="shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors"
              classList={{
                "border-accent bg-accent/10 text-accent": isActive(),
                "border-border/60 text-muted-foreground hover:border-accent/50 hover:text-foreground":
                  !isActive(),
              }}
              onClick={() => props.onSelect(index())}
              onKeyDown={(e) => {
                const last = props.pages().length - 1;
                if (e.key === "ArrowRight") {
                  e.preventDefault();
                  props.onSelect(Math.min(props.active() + 1, last));
                } else if (e.key === "ArrowLeft") {
                  e.preventDefault();
                  props.onSelect(Math.max(props.active() - 1, 0));
                }
              }}
            >
              {page.name}
            </button>
          );
        }}
      </For>
    </div>
  );
}

/**
 * The product grid as a horizontal carousel: swiping pages between category
 * tabs (premium, on the shared gesture standard) while taps still pick a
 * product. `touch-action: pan-y` keeps native vertical scroll; the recognizer
 * claims the horizontal axis. A swipe that engaged suppresses the resulting
 * product tap so paging never adds a line by accident.
 */
function ProductGrid(props: {
  pages: Accessor<CategoryPage[]>;
  activePage: Accessor<number>;
  setActivePage: (index: number) => void;
  products: Accessor<MenuProduct[]>;
  selectedId: Accessor<string | null>;
  onPick: (product: MenuProduct) => void;
}) {
  const { t } = useI18n();
  let surfaceRef: HTMLDivElement | undefined;

  const swipe = useMenuCategorySwipe({
    index: () => props.activePage(),
    count: () => props.pages().length,
    onChange: (next) => props.setActivePage(next),
    getSurfaceRef: () => surfaceRef,
  });

  return (
    <div
      ref={(el) => {
        surfaceRef = el;
      }}
      class="overflow-hidden"
      style={{ "touch-action": "pan-y" }}
      onPointerDown={swipe.handlePointerDown}
      onPointerMove={swipe.handlePointerMove}
      onPointerUp={swipe.handlePointerUp}
      onPointerCancel={swipe.handlePointerCancel}
    >
      <div
        class="grid grid-cols-2 gap-3 will-change-transform sm:grid-cols-3"
        style={{
          transform: `translate3d(${swipe.dragX()}px, 0, 0)`,
          transition: swipe.isAnimating() ? `transform ${swipe.TRANSITION_MS}ms ease-out` : "none",
        }}
      >
        <Show
          when={props.products().length > 0}
          fallback={
            <p
              class="col-span-full py-8 text-center text-sm text-muted-foreground"
              data-testid="pos-category-empty"
            >
              {t(msg`No products in this category.`)}
            </p>
          }
        >
          <For each={props.products()}>
            {(p) => (
              <button
                type="button"
                data-testid="pos-product"
                data-product-name={p.name}
                aria-pressed={props.selectedId() === p.id}
                class="flex min-h-20 flex-col justify-between rounded-xl border border-border/60 bg-surface p-3.5 text-left transition-[transform,border-color,background-color] hover:border-accent/60 hover:bg-accent/5 active:scale-[0.98]"
                classList={{
                  "ring-2 ring-accent border-accent/70 bg-accent/5": props.selectedId() === p.id,
                }}
                onClick={() => {
                  // A horizontal swipe captured the pointer; don't also pick.
                  if (swipe.isSwiping()) {
                    return;
                  }
                  props.onPick(p);
                }}
              >
                <div class="text-sm font-semibold leading-tight">{p.name}</div>
                <div class="mt-2 font-numeric text-sm font-bold text-accent">
                  {formatMoney(p.price)}
                </div>
              </button>
            )}
          </For>
        </Show>
      </div>
    </div>
  );
}
